import { createSlice } from '@reduxjs/toolkit'
import {
  addRisque,
  deleteRisque,
  fetchActionsDistribution,
  fetchRisksFromRelatedControls,
  getRisqueByActionId,
  getRisqueByPlanId,
  getRisquebyId,
  getRisques,
  getRisquesByMissionId,
  removeRisquesFromAction,
  updateRisque,
  extractRisksFromFile,
} from './risqueActions'
import {
  ActionsDistributionResponse,
  Risque,
  RisqueResponse,
} from '../../types/risqueTypes'

interface RisqueState {
  risques: Risque[]
  relatedRisks: Risque[]
  loading: boolean
  risque: Risque | null
  error: string | null
  addedRisque: Risque | null
  updatedRisque: Risque | null
  RisqueResponse: RisqueResponse | null
  sourceRisques: Risque[]
  RisquesbyPlan: Risque[]
  actionsDistribution: ActionsDistributionResponse | null
}

const initialState: RisqueState = {
  relatedRisks: [],
  risques: [],
  loading: false,
  error: null,
  addedRisque: null,
  risque: null,
  updatedRisque: null,
  RisqueResponse: null,
  sourceRisques: [],
  RisquesbyPlan: [],
  actionsDistribution: null,
}

export const risqueSlice = createSlice({
  name: 'risque',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRisques.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getRisques.fulfilled, (state, action) => {
        state.loading = false
        state.RisqueResponse = {
          risques: action.payload.risques,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      .addCase(getRisques.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(fetchRisksFromRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRisksFromRelatedControls.fulfilled, (state, action) => {
        state.loading = false
        const risks = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.relatedRisks = risks
      })
      .addCase(fetchRisksFromRelatedControls.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Something went wrong'
      })
      .addCase(deleteRisque.fulfilled, (state, action) => {
        state.loading = false
        const deletedRisqueId = action.meta.arg

        if (state.RisqueResponse) {
          state.RisqueResponse.risques = (
            state.RisqueResponse.risques ?? []
          ).filter((risque) => risque._id !== deletedRisqueId)

          state.RisqueResponse.total = Math.max(
            0,
            (state.RisqueResponse.total ?? 0) - 1
          )
          state.RisqueResponse.totalPages = state.RisqueResponse.totalPages ?? 0
          state.RisqueResponse.currentPage =
            state.RisqueResponse.currentPage ?? 0
        }
      })
      .addCase(addRisque.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addRisque.fulfilled, (state, action) => {
        state.loading = false
        state.addedRisque = action.payload
        state.risques.push(action.payload)
      })
      .addCase(addRisque.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(updateRisque.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateRisque.fulfilled, (state, action) => {
        state.loading = false
        state.updatedRisque = action.payload
      })
      .addCase(updateRisque.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getRisquesByMissionId.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(getRisquesByMissionId.fulfilled, (state, action) => {
        state.loading = false
        const missionRisques = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.risques = missionRisques as []
      })
      .addCase(getRisquesByMissionId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getRisquebyId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getRisquebyId.fulfilled, (state, action) => {
        state.loading = false
        state.risque = action.payload
      })
      .addCase(getRisquebyId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getRisqueByActionId.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(getRisqueByActionId.fulfilled, (state, action) => {
        state.loading = false
        const sourceRisques = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.sourceRisques = sourceRisques as []
      })

      .addCase(getRisqueByActionId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(removeRisquesFromAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(removeRisquesFromAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(removeRisquesFromAction.fulfilled, (state, action) => {
        state.loading = false
        state.risques = action.payload.risques
      })
      .addCase(getRisqueByPlanId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getRisqueByPlanId.fulfilled, (state, action) => {
        state.loading = false
        const planRisques = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )

        state.RisquesbyPlan = planRisques as []
      })
      .addCase(getRisqueByPlanId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(fetchActionsDistribution.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchActionsDistribution.fulfilled, (state, action) => {
        state.loading = false
        state.actionsDistribution = action.payload
      })
      .addCase(fetchActionsDistribution.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Something went wrong'
      })
      .addCase(extractRisksFromFile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(extractRisksFromFile.fulfilled, (state, action) => {
        state.loading = false
        state.RisquesbyPlan = [...state.RisquesbyPlan, ...action.payload]
      })
      .addCase(extractRisksFromFile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Something went wrong'
      })
  },
})

export default risqueSlice.reducer
